import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { SpecificationStreamContent, SpecificationStreamProvider } from '../../types/specification';
import type { SpecificationConfirmDisclosureRepository } from '../../services/specification-confirm-disclosure-repository';
import { SpecificationOperateType } from '../../enums';
import type { SpecificationConfirmDisclosurePayload } from '../../types/specification-confirm-disclosure';
import { GetClassificationApi } from '../../apis/get-classification';
import { GenTechnicalApi } from '../../apis/gen-technical';
import { GenTechnologyCategoryApi } from '../../apis/gen-technology-category';
import { SubmitConfirmDisclosureContentApi } from '../../apis/submit-confirm-disclosure-content';
import { http2StreamProvider, SSE2StreamProvider } from '../../utils';
import { GenEmbodimentApi } from '../../apis/gen-embodiment-api';
import _ from 'lodash';

@injectable()
export class SpecificationConfirmDisclosureRepositoryImpl implements SpecificationConfirmDisclosureRepository {
    constructor(
        @inject(GetClassificationApi)
        private _getClassificationApi: GetClassificationApi,
        @inject(GenTechnologyCategoryApi)
        private _genTechnologyCategoryApi: GenTechnologyCategoryApi,
        @inject(GenTechnicalApi)
        private _genTechnicalApi: GenTechnicalApi,
        @inject(SubmitConfirmDisclosureContentApi)
        private _submitConfirmDisclosureContentApi: SubmitConfirmDisclosureContentApi,
        @inject(GenEmbodimentApi)
        private _genEmbodimentApi: GenEmbodimentApi,
    ) {}

    genClassification(taskId: string, regenerate?: boolean): SpecificationStreamProvider<SpecificationStreamContent<string[]>> {
        return http2StreamProvider((options) => {
            return this._getClassificationApi.getClassification({
                task_id: taskId,
                operate_type: regenerate ? SpecificationOperateType.Regenerate : undefined,
            }, options);
        }, data => data?.classification || []);
    }

    genStatutoryCategory(taskId: string, regenerate?: boolean): SpecificationStreamProvider<SpecificationStreamContent<string>> {
        const sse = this._genTechnologyCategoryApi.genTechnologyCategory({
            task_id: taskId,
            operate_type: regenerate ? SpecificationOperateType.Regenerate : undefined,
        });
        return SSE2StreamProvider(sse, data => data || '');
    }

    genTechnical(taskId: string, regenerate?: boolean): SpecificationStreamProvider<SpecificationStreamContent<{
        technical_problem: string;
        technical_methods: string;
        benefit: string;
    }>> {
        const sse = this._genTechnicalApi.genTechnical({
            task_id: taskId,
            operate_type: regenerate ? SpecificationOperateType.Regenerate : undefined,
        });
        return SSE2StreamProvider(sse, data => data || {
            technical_problem: '',
            technical_methods: '',
            benefit: '',
        });
    }

    genEmbodiment(taskId: string, regenerate?: boolean): SpecificationStreamProvider<SpecificationStreamContent<{
        text: string;
        selected: boolean;
    }[]>> {
        const sse = this._genEmbodimentApi.genEmbodiment({
            task_id: taskId,
            operate_type: regenerate ? SpecificationOperateType.Regenerate : undefined,
        });
        return SSE2StreamProvider(sse, (data) => {
            return _.map(data?.embodiments, (_) => {
                return {
                    text: _.text,
                    selected: _.selected,
                };
            });
        });
    }

    async submit(taskId: string, payload: SpecificationConfirmDisclosurePayload): Promise<void> {
        await this._submitConfirmDisclosureContentApi.submitConfirmDisclosureContent({
            task_id: taskId,
            classification: payload.classification,
            category: payload.statutoryCategory,
            technical_problem: payload.technicalQuestion,
            technical_methods: payload.technicalSolution,
            benefit: payload.technicalBenefit,
            embodiments: payload.embodiments,
        });
    }
}
